import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";

const categories = [
  {
    id: "wigs",
    name: "Signature Wigs",
    count: "24 styles",
    desc: "Lace-front, glueless, and full-cap silhouettes — built for everyday luxury.",
    img: "https://images.pexels.com/photos/14950285/pexels-photo-14950285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-7 lg:row-span-2",
    height: "h-[420px] lg:h-full",
  },
  {
    id: "bundles",
    name: "Raw Bundles",
    count: "12 textures",
    desc: "Single-donor, ethically sourced, naturally vibrant.",
    img: "https://images.unsplash.com/photo-1745975980824-bc88bd400c78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxoYWlyJTIwYnVuZGxlcyUyMGV4dGVuc2lvbnMlMjBsYWNlfGVufDB8fHx8MTc3NzQ0NzAxMHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
    height: "h-[320px]",
  },
  {
    id: "lace",
    name: "Lace & Closures",
    count: "8 finishes",
    desc: "Hand-ventilated, undetectable lace — pre-plucked, ready to wear.",
    img: "https://images.pexels.com/photos/6923472/pexels-photo-6923472.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-5",
    height: "h-[320px]",
  },
];

const Card = ({ cat, i }) => (
  <motion.a
    href="#waitlist"
    data-testid={`category-card-${cat.id}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8, delay: i * 0.12, ease: [0.25, 0.1, 0.25, 1] }}
    className={`bento-card group block ${cat.span} ${cat.height}`}
  >
    <div className="relative w-full h-full">
      <img
        src={cat.img}
        alt={cat.name}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0705] via-[#0a0705]/40 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0a0705]/70 to-transparent" />

      <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] gold-text font-body">
          {cat.count}
        </span>
        <div className="w-10 h-10 border border-white/15 flex items-center justify-center backdrop-blur-md bg-black/30 group-hover:bg-[#E6CC98] group-hover:border-[#E6CC98] transition-all duration-500">
          <ArrowUpRight
            size={16}
            weight="thin"
            className="text-[#FDFBF7] group-hover:text-[#0a0705] transition-colors duration-500"
          />
        </div>
      </div>

      <div className="absolute bottom-6 left-6 right-6">
        <h3 className="font-heading text-3xl sm:text-4xl text-[#FDFBF7] tracking-tight mb-2">
          {cat.name}
        </h3>
        <p className="text-sm text-[#A39791] font-body max-w-md leading-relaxed">
          {cat.desc}
        </p>
      </div>
    </div>
  </motion.a>
);

export const Collection = () => {
  return (
    <section
      id="collection"
      data-testid="collection-section"
      className="relative bg-[#0a0705] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#E6CC98]" />
              <span className="text-xs uppercase tracking-[0.3em] gold-text font-body">
                The Collection
              </span>
            </div>
            <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FDFBF7] leading-[1.05] max-w-2xl">
              Curated categories,
              <br />
              <span className="italic gold-gradient-text">
                crafted with intention.
              </span>
            </h2>
          </div>
          <p className="max-w-md text-base text-[#A39791] font-body leading-relaxed">
            From signature wigs to raw bundles, every piece is sourced, sewn,
            and finished by master artisans across our American ateliers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[320px]">
          {categories.map((cat, i) => (
            <Card key={cat.id} cat={cat} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
